import { DragDropContext } from '@hello-pangea/dnd';
import { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { DragDropAnswerContext } from './DragDropAnswerContext';
import { DropSlot } from './DropSlot';
import { OptionChip } from './OptionChip';
import { TokenBank } from './TokenBank';
import { useDndA11y } from '@/hooks/questions/dnd/useDndA11y';
import {
  useDragDropAnswer,
  UseDragDropAnswerArgs,
} from '@/hooks/questions/dnd/useDragDropAnswer';
import { WithClassname } from '@/components/ui/types';
import { Flow } from '@/utils/questions/dnd/types';

type DragDropAnswerRootProps = PropsWithChildren<
  UseDragDropAnswerArgs & WithClassname & { flow?: Flow }
>;

function DragDropAnswerRoot({
  tokens,
  slots,
  placement,
  onPlacementChange,
  flow = 'horizontal',
  disabled,
  className,
  children,
}: DragDropAnswerRootProps) {
  const { onDragEnd, ...contextValue } = useDragDropAnswer({
    tokens,
    slots,
    placement,
    onPlacementChange,
    disabled,
  });
  const {
    dragHandleUsageInstructions,
    announceDragStart,
    announceDragUpdate,
    announceDragEnd,
  } = useDndA11y(contextValue.getToken);

  return (
    <DragDropAnswerContext.Provider value={{ ...contextValue, flow }}>
      <DragDropContext
        dragHandleUsageInstructions={dragHandleUsageInstructions}
        onDragStart={(start, provided) => {
          if (contextValue.selectedId) contextValue.clearSelection();
          if (window.navigator.vibrate) {
            window.navigator.vibrate(100);
          }
          announceDragStart(start, provided);
        }}
        onDragUpdate={announceDragUpdate}
        onDragEnd={(result, provided) => {
          onDragEnd(result);
          announceDragEnd(result, provided);
        }}
      >
        <div className={twMerge('flex w-full flex-col gap-6', className)}>
          {children}
        </div>
      </DragDropContext>
    </DragDropAnswerContext.Provider>
  );
}

export const DragDropAnswer = Object.assign(DragDropAnswerRoot, {
  Bank: TokenBank,
  Slot: DropSlot,
  Chip: OptionChip,
});
